import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/formatters';

export const GlassInput = forwardRef(({
  className,
  label,
  error,
  helperText,
  icon: Icon,
  iconPosition = 'left',
  rightElement,
  variant = 'default',
  size = 'md',
  ...props
}, ref) => {
  const variants = {
    default: 'bg-white/5 border-white/10 focus:border-accent-primary/50 focus:bg-white/[0.08]',
    filled: 'bg-white/10 border-transparent focus:bg-white/[0.15]',
    outlined: 'bg-transparent border-white/20 focus:border-accent-primary',
    glass: 'glass focus:bg-white/10',
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-sm',
    lg: 'px-5 py-4 text-base',
  };

  const inputClasses = cn(
    'w-full rounded-xl text-white/90 placeholder:text-white/40',
    'border transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-accent-primary/20',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    variants[variant],
    sizes[size],
    Icon && iconPosition === 'left' && 'pl-11',
    rightElement && 'pr-11',
    error && 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20',
    className
  );

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-white/70 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <input ref={ref} className={inputClasses} {...props} />
        {Icon && iconPosition === 'right' && (
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40">
            <Icon className="w-5 h-5" />
          </div>
        )}
        {rightElement && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightElement}
          </div>
        )}
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-sm text-red-400"
        >
          {error}
        </motion.p>
      )}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-white/50">{helperText}</p>
      )}
    </div>
  );
});

export const GlassTextarea = forwardRef(({
  className,
  label,
  error,
  helperText,
  rows = 4,
  ...props
}, ref) => (
  <div className="w-full">
    {label && (
      <label className="block text-sm font-medium text-white/70 mb-2">
        {label}
      </label>
    )}
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'w-full rounded-xl px-4 py-3 text-sm text-white/90 placeholder:text-white/40',
        'bg-white/5 border border-white/10',
        'transition-all duration-300 resize-none',
        'focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary/50 focus:bg-white/[0.08]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        error && 'border-red-500/50 focus:border-red-500',
        className
      )}
      {...props}
    />
    {error && (
      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-1.5 text-sm text-red-400"
      >
        {error}
      </motion.p>
    )}
    {helperText && !error && (
      <p className="mt-1.5 text-sm text-white/50">{helperText}</p>
    )}
  </div>
));

export const GlassSelect = forwardRef(({
  className,
  label,
  error,
  helperText,
  options = [],
  placeholder = 'Selecione...',
  ...props
}, ref) => (
  <div className="w-full">
    {label && (
      <label className="block text-sm font-medium text-white/70 mb-2">
        {label}
      </label>
    )}
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          'w-full rounded-xl px-4 py-3 text-sm text-white/90',
          'bg-white/5 border border-white/10 appearance-none',
          'transition-all duration-300',
          'focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary/50 focus:bg-white/[0.08]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          error && 'border-red-500/50 focus:border-red-500',
          className
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled className="bg-slate-900">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="bg-slate-900"
          >
            {option.label}
          </option>
        ))}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
    {error && (
      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-1.5 text-sm text-red-400"
      >
        {error}
      </motion.p>
    )}
    {helperText && !error && (
      <p className="mt-1.5 text-sm text-white/50">{helperText}</p>
    )}
  </div>
));

GlassInput.displayName = 'GlassInput';
GlassTextarea.displayName = 'GlassTextarea';
GlassSelect.displayName = 'GlassSelect';

export default GlassInput;
